import { QueryClient, useQuery } from "@tanstack/react-query";
import type { ChatConversation, ChatPerson } from "@ide/shared";
import { ApiError, get } from "./api";
import type {
  AppConfig,
  DevCard,
  DevList,
  DiscoverLive,
  Friend,
  Experience,
  Group,
  Invite,
  Me,
  Member,
  Notification,
  PersonRef,
  PortfolioItem,
  ProjectDetail,
  ProjectListItem,
  PublicProfile,
  PublicProject,
  SessionDetail,
  SessionSummary,
  Team,
} from "./types";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      refetchOnWindowFocus: true,
      // Não insiste em erros de cliente (401/403/404): só em falhas de rede/5xx.
      retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
    },
  },
});

export const keys = {
  config: ["config"] as const,
  me: ["me"] as const,
  projects: ["projects"] as const,
  project: (id: string) => ["project", id] as const,
  members: (id: string) => ["project", id, "members"] as const,
  invites: (id: string) => ["project", id, "invites"] as const,
  sessions: (id: string) => ["project", id, "sessions"] as const,
  session: (id: string) => ["session", id] as const,
  skills: ["skills"] as const,
  devs: (params: string) => ["devs", params] as const,
  discoverLive: ["discover", "live"] as const,
  friends: ["friends"] as const,
  teams: ["teams"] as const,
  projectTeam: (id: string) => ["project", id, "team"] as const,
  match: (skills: string) => ["match", skills] as const,
  publicProjects: (q: string) => ["public-projects", q] as const,
  profile: (handle: string) => ["profile", handle] as const,
  feed: ["feed"] as const,
  groups: ["groups"] as const,
  group: (slug: string) => ["group", slug] as const,
  notifications: ["notifications"] as const,
  devices: ["devices"] as const,
  templates: ["templates"] as const,
  experiences: ["me", "experiences"] as const,
  portfolio: ["me", "portfolio"] as const,
  blocks: ["me", "blocks"] as const,
  follows: (handle: string, dir: "followers" | "following") => ["follows", handle, dir] as const,
  contacts: (q: string) => ["contacts", q] as const,
  conversations: ["chat", "conversations"] as const,
  conversation: (id: string) => ["chat", "conversation", id] as const,
  messages: (id: string) => ["chat", "messages", id] as const,
  chatUnread: ["chat", "unread"] as const,
};

export const useConfig = () =>
  useQuery({ queryKey: keys.config, queryFn: () => get<AppConfig>("/config"), staleTime: Infinity });

export const useMe = () =>
  useQuery({
    queryKey: keys.me,
    queryFn: async () => {
      try {
        return await get<Me>("/me");
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 60_000,
  });

export const useProjects = (enabled = true) =>
  useQuery({
    queryKey: keys.projects,
    queryFn: () => get<{ projects: ProjectListItem[] }>("/projects").then((r) => r.projects),
    enabled,
  });

export const useProject = (id: string) =>
  useQuery({
    queryKey: keys.project(id),
    queryFn: () => get<ProjectDetail>(`/projects/${id}`),
    enabled: Boolean(id),
  });

export const useMembers = (id: string, enabled = true) =>
  useQuery({
    queryKey: keys.members(id),
    queryFn: () => get<{ members: Member[] }>(`/projects/${id}/members`).then((r) => r.members),
    enabled: Boolean(id) && enabled,
  });

export const useInvites = (id: string, enabled: boolean) =>
  useQuery({
    queryKey: keys.invites(id),
    queryFn: () => get<{ invites: Invite[] }>(`/projects/${id}/invites`).then((r) => r.invites),
    enabled,
  });

export const useSessions = (id: string) =>
  useQuery({
    queryKey: keys.sessions(id),
    queryFn: () => get<{ sessions: SessionSummary[] }>(`/projects/${id}/sessions`).then((r) => r.sessions),
  });

export const useSession = (id: string) =>
  useQuery({
    queryKey: keys.session(id),
    queryFn: () => get<SessionDetail>(`/sessions/${id}`),
    enabled: Boolean(id),
  });

export const useSkills = () =>
  useQuery({
    queryKey: keys.skills,
    queryFn: () => get<{ skills: Array<{ slug: string; label: string }> }>("/skills").then((r) => r.skills),
    staleTime: Infinity,
  });

export const useDevs = (params: URLSearchParams, enabled = true) =>
  useQuery({
    queryKey: keys.devs(params.toString()),
    queryFn: () => get<DevList>(`/discover/devs?${params.toString()}`),
    enabled,
    placeholderData: (prev) => prev,
    // A presença muda o tempo todo: a lista se atualiza sozinha enquanto está aberta.
    refetchInterval: 30_000,
  });

/** Faixa viva da página Descobrir: quem está online, sessões abertas e o mural de ajuda. */
export const useDiscoverLive = (enabled = true) =>
  useQuery({
    queryKey: keys.discoverLive,
    queryFn: () => get<DiscoverLive>("/discover/live"),
    enabled,
    refetchInterval: 15_000,
  });

export const useMatch = (skills: string, enabled: boolean) =>
  useQuery({
    queryKey: keys.match(skills),
    queryFn: () =>
      get<{ matches: DevCard[]; me?: DevList["me"] }>(`/discover/match?skills=${encodeURIComponent(skills)}`),
    enabled,
  });

export const usePublicProjects = (q: string) =>
  useQuery({
    queryKey: keys.publicProjects(q),
    queryFn: () =>
      get<{ projects: PublicProject[] }>(`/discover/projects?q=${encodeURIComponent(q)}`).then(
        (r) => r.projects,
      ),
    placeholderData: (prev) => prev,
  });

export const useProfile = (handle: string) =>
  useQuery({ queryKey: keys.profile(handle), queryFn: () => get<PublicProfile>(`/users/${handle}`) });

export interface FeedItem {
  type: "session" | "project_created";
  at: string;
  id: string;
  projectId?: string;
  projectName?: string;
  name?: string;
  ownerHandle?: string;
  ownerName?: string;
  startedAt?: string;
  endedAt?: string | null;
  participants?: Array<{ handle: string; name: string; avatarUrl: string | null }> | null;
}

export const useFeed = () =>
  useQuery({ queryKey: keys.feed, queryFn: () => get<{ items: FeedItem[] }>("/feed").then((r) => r.items) });

export const useGroups = (enabled = true) =>
  useQuery({
    queryKey: keys.groups,
    enabled,
    queryFn: () => get<{ groups: Group[] }>("/groups").then((r) => r.groups),
  });

export const useGroup = (slug: string) =>
  useQuery({
    queryKey: keys.group(slug),
    queryFn: () =>
      get<{
        group: Group & { createdAt: string };
        myRole: string | null;
        members: Array<Omit<Member, "role"> & { role: "owner" | "admin" | "member" }>;
        projects: Array<{
          id: string;
          name: string;
          description: string | null;
          visibility: "private" | "unlisted" | "public";
          runtime: string;
          updatedAt: string;
          /** Seu papel no projeto (null: não participa). */
          myRole: string | null;
          members: number;
          /** Dá para abrir (participa, ou é aberto); senão, "Pedir acesso". */
          canOpen: boolean;
        }>;
      }>(`/groups/${slug}`),
  });

export const useNotifications = (enabled: boolean, interval = 60_000) =>
  useQuery({
    queryKey: keys.notifications,
    queryFn: () => get<{ items: Notification[]; unread: number }>("/notifications"),
    enabled,
    refetchInterval: interval,
    refetchOnWindowFocus: true,
  });

export const useDevices = () =>
  useQuery({
    queryKey: keys.devices,
    queryFn: () =>
      get<{
        devices: Array<{
          id: string;
          client: string;
          userAgent: string | null;
          ip: string | null;
          createdAt: string;
          lastUsedAt: string;
          current: boolean;
        }>;
      }>("/me/devices").then((r) => r.devices),
  });

export const useTemplates = () =>
  useQuery({
    queryKey: keys.templates,
    queryFn: () =>
      get<{ templates: Array<{ id: string; label: string; description: string; runtime: string }> }>(
        "/templates",
      ).then((r) => r.templates),
    staleTime: Infinity,
  });

// ───────────── Perfil profissional ─────────────

export const useExperiences = () =>
  useQuery({
    queryKey: keys.experiences,
    queryFn: () => get<{ experiences: Experience[] }>("/me/experiences").then((r) => r.experiences),
  });

export const usePortfolio = () =>
  useQuery({
    queryKey: keys.portfolio,
    queryFn: () => get<{ items: PortfolioItem[] }>("/me/portfolio").then((r) => r.items),
  });

export const useBlocks = () =>
  useQuery({
    queryKey: keys.blocks,
    queryFn: () =>
      get<{
        blocked: Array<{ id: string; handle: string; name: string; avatarUrl: string | null; at: string }>;
      }>("/me/blocks").then((r) => r.blocked),
  });

export const useFollows = (handle: string, dir: "followers" | "following", enabled: boolean) =>
  useQuery({
    queryKey: keys.follows(handle, dir),
    queryFn: () => get<{ people: PersonRef[] }>(`/users/${handle}/${dir}`).then((r) => r.people),
    enabled,
  });

export const useContacts = (q: string) =>
  useQuery({
    queryKey: keys.contacts(q),
    queryFn: () =>
      get<{ people: Array<PersonRef & { contact: boolean }> }>(
        `/people/contacts?q=${encodeURIComponent(q)}`,
      ).then((r) => r.people),
    placeholderData: (prev) => prev,
  });

// ───────────── Chat ─────────────

export const useConversations = (enabled = true) =>
  useQuery({
    queryKey: keys.conversations,
    queryFn: () => get<{ conversations: ChatConversation[]; unread: number }>("/chat/conversations"),
    enabled,
    // O WebSocket atualiza em tempo real; o intervalo só cobre quedas de conexão.
    refetchInterval: 60_000,
  });

export const useConversation = (id: string | null) =>
  useQuery({
    queryKey: keys.conversation(id ?? ""),
    queryFn: () =>
      get<{
        conversation: ChatConversation;
        members: Array<
          ChatPerson & { role: "owner" | "admin" | "member"; online: boolean; lastReadMessageId: number }
        >;
      }>(`/chat/conversations/${id}`),
    enabled: Boolean(id),
  });

export const useChatUnread = (enabled: boolean) =>
  useQuery({
    queryKey: keys.chatUnread,
    queryFn: () => get<{ unread: number }>("/chat/unread").then((r) => r.unread),
    enabled,
    refetchInterval: 60_000,
  });

/** Amigos (seguem um ao outro): os únicos que entram num time. */
export const useFriends = (enabled = true) =>
  useQuery({
    queryKey: keys.friends,
    queryFn: () => get<{ friends: Friend[] }>("/me/friends").then((r) => r.friends),
    enabled,
  });

/** Seus times (grupos formados entre amigos), com os integrantes. */
export const useTeams = (enabled = true) =>
  useQuery({
    queryKey: keys.teams,
    queryFn: () => get<{ teams: Team[] }>("/teams").then((r) => r.teams),
    enabled,
    refetchInterval: 30_000,
  });
